import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "../../supabase/supabase";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import {
  SIDEBAR_TOGGLES,
  navFlag,
} from "@/components/dashboard/layout/navItems";

type Organization = {
  id: string;
  name: string;
  is_pro: boolean;
  feature_flags?: Record<string, boolean>;
};

// Call backends. New organizations start with only V2 voice on; the API server refuses a call
// on a backend that is off. Which pages an organization has is the Sidebar card below
// (nav.* flags); the old per-page flags here only locked sidebar items and were folded into it.
const AVAILABLE_FEATURES = [
  {
    id: "voice_v2",
    name: "V2 Voice Calls",
    description: "Phone calls and web sessions on the V2 agent server",
  },
  {
    id: "voice_v1",
    name: "V1 Voice Calls",
    description: "Phone calls and web audio on the V1 (LiveKit) agent",
  },
  {
    id: "video_v1",
    name: "V1 Video Interviews",
    description: "Video interview links and video sessions on the V1 (LiveKit) agent",
  },
  {
    id: "hirevox_publish",
    name: "Hirevox Publish",
    description: "Publish button on assistants, which pushes them to Hirevox",
    // Opt-in: only organizations that use Hirevox turn this on
    defaultEnabled: false,
  },
];

const OrganizationFeaturesPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  // Toggles save as they change: saves run one at a time in toggle order, each writing the
  // flags as of that toggle, so the last write is the latest state
  const [pendingSaves, setPendingSaves] = useState(0);
  const flagsRef = useRef<Record<string, boolean>>({});
  const saveQueue = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    if (id) {
      fetchOrganization(id);
    }
  }, [id]);

  const fetchOrganization = async (orgId: string, quiet = false) => {
    if (!quiet) setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("organizations")
        .select("*")
        .eq("id", orgId)
        .single();

      if (error) throw error;

      if (data) {
        setOrganization(data);
        // Initialize feature flags with defaults for any missing flags
        const flags = { ...(data.feature_flags || {}) };

        // Ensure all available features have a value (default to true unless the feature says otherwise)
        AVAILABLE_FEATURES.forEach((feature) => {
          if (flags[feature.id] === undefined) {
            flags[feature.id] = feature.defaultEnabled ?? true;
          }
        });
        SIDEBAR_TOGGLES.forEach((item) => {
          if (flags[navFlag(item.key)] === undefined) {
            flags[navFlag(item.key)] = true;
          }
        });

        flagsRef.current = flags;
        setFeatureFlags(flags);
      }
    } catch (error) {
      console.error("Error fetching organization:", error);
      toast({
        title: "Error",
        description: "Failed to fetch organization details",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFeatureToggle = (featureId: string, enabled: boolean) => {
    if (!organization) return;
    const flags = { ...flagsRef.current, [featureId]: enabled };
    flagsRef.current = flags;
    setFeatureFlags(flags);

    setPendingSaves((n) => n + 1);
    saveQueue.current = saveQueue.current.then(async () => {
      try {
        const { error } = await supabase
          .from("organizations")
          .update({ feature_flags: flags })
          .eq("id", organization.id);

        if (error) throw error;
      } catch (error) {
        console.error("Error updating feature flags:", error);
        toast({
          title: "Error",
          description: "Failed to save the change, showing the saved settings again",
          variant: "destructive",
        });
        // Back to what the database holds
        await fetchOrganization(organization.id, true);
      } finally {
        setPendingSaves((n) => n - 1);
      }
    });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 px-4 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p>Loading organization details...</p>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Button variant="outline" onClick={() => navigate("/superadmin")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Super Admin
        </Button>
        <Card className="mt-6">
          <CardContent className="p-6 text-center">
            <p>Organization not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <Button variant="outline" onClick={() => navigate("/superadmin")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Super Admin
          </Button>
          <h1 className="text-3xl font-bold mt-4">{organization.name}</h1>
          <p className="text-muted-foreground">
            Manage feature flags for this organization
          </p>
        </div>
        <p className="flex items-center text-sm text-muted-foreground">
          {pendingSaves > 0 ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check className="mr-2 h-4 w-4" />
              Changes save automatically
            </>
          )}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Call Backends</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {AVAILABLE_FEATURES.map((feature) => (
              <div key={feature.id}>
                <div className="flex items-center justify-between">
                  <div>
                    <Label
                      htmlFor={feature.id}
                      className="text-base font-medium"
                    >
                      {feature.name}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                  <Switch
                    id={feature.id}
                    checked={featureFlags[feature.id] || false}
                    onCheckedChange={(checked) =>
                      handleFeatureToggle(feature.id, checked)
                    }
                  />
                </div>
                <Separator className="mt-4" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Sidebar</CardTitle>
          <p className="text-sm text-muted-foreground">
            Items this organization's users see in the left sidebar. A hidden
            item's pages are blocked too. Platform admins always see everything.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
            {SIDEBAR_TOGGLES.map((item) => {
              const flag = navFlag(item.key);
              return (
                <div
                  key={flag}
                  className="flex items-center justify-between gap-4 border-b py-3"
                >
                  <div className="min-w-0">
                    <Label htmlFor={flag} className="text-sm font-medium">
                      {item.label}
                    </Label>
                    <p className="truncate text-xs text-muted-foreground">
                      {item.href}
                    </p>
                  </div>
                  <Switch
                    id={flag}
                    checked={featureFlags[flag] || false}
                    onCheckedChange={(checked) =>
                      handleFeatureToggle(flag, checked)
                    }
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>PRO Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="pro-status" className="text-base font-medium">
                PRO Features
              </Label>
              <p className="text-sm text-muted-foreground">
                Enable or disable PRO features for this organization
              </p>
            </div>
            <Switch
              id="pro-status"
              checked={organization.is_pro}
              onCheckedChange={async (checked) => {
                try {
                  const { error } = await supabase
                    .from("organizations")
                    .update({ is_pro: checked })
                    .eq("id", organization.id);

                  if (error) throw error;

                  setOrganization({
                    ...organization,
                    is_pro: checked,
                  });

                  toast({
                    title: "Success",
                    description: `PRO status ${checked ? "enabled" : "disabled"} successfully`,
                    variant: "default",
                  });
                } catch (error) {
                  console.error("Error updating PRO status:", error);
                  toast({
                    title: "Error",
                    description: "Failed to update PRO status",
                    variant: "destructive",
                  });
                }
              }}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrganizationFeaturesPage;
